/**
 * ตรรกะล้วนของสองหน้าจอ Kanban/Pull (D-102) — แยกออกจาก JSX เพื่อให้ทดสอบกฎได้ตรง ๆ
 * แนวเดียวกับ `lib/procurement/procurementView.ts`
 *
 * ครึ่งบนเป็นของ `ReplenishmentPolicyScreen` (`KANBAN_CONFIG`): policy + control
 * cycle + card ครึ่งล่างเป็นของ `KanbanSignalScreen` (`KANBAN_EXECUTION`): signal
 * lifecycle ทั้งห้า transition
 *
 * ⚠️ ไฟล์นี้ **ไม่มีฟังก์ชันใดตัดสินว่าใครกดปุ่ม "อนุมัติ" ได้ตามเป็นตัวเอง** — D-010
 * ต้องให้เซิร์ฟเวอร์เป็นผู้ปฏิเสธและ**หน้าจอเรนเดอร์คำปฏิเสธนั้น** ไม่ใช่เดาล่วงหน้า
 * แล้วซ่อนปุ่ม (invariant ของ `WO-P2-M7-006`: "a hidden control teaches nothing, a
 * refused one teaches the rule") `policyLifecycleActionsFor` จึงพิจารณาแค่ `status`
 * ไม่รับ "ใครคือผู้ใช้ปัจจุบัน" เข้ามาเลย
 */

import type {
  KanbanSignalTransition,
  PolicyTransition,
} from './client';
import {
  ACKNOWLEDGED_SIGNAL_STATUS,
  ACTIVE_CYCLE_STATUS,
  ACTIVE_POLICY_STATUS,
  AVAILABLE_CARD_STATUS,
  BLOCKED_CARD_STATUS,
  CANCELLED_CYCLE_STATUS,
  CANCELLED_POLICY_STATUS,
  CANCELLED_SIGNAL_STATUS,
  DRAFT_POLICY_STATUS,
  EMPTY_CARD_STATUS,
  FULL_CARD_STATUS,
  IN_REPLENISHMENT_SIGNAL_STATUS,
  NEW_SIGNAL_STATUS,
  PULL_MODE,
  PUSH_MODE,
  REPLENISHED_SIGNAL_STATUS,
  REPLENISHING_CARD_STATUS,
  SUPERSEDED_CYCLE_STATUS,
  SUPERSEDED_POLICY_STATUS,
  type CreateReplenishmentPolicyBody,
  type KanbanContainerLotInput,
  type KanbanSignal,
  type ReplenishmentPolicy,
} from './types';

/* ================================================ Replenishment Policy (D-102) === */

export interface StatusDescriptor {
  readonly code: string;
  readonly label: string;
  readonly summary: string;
}

export const REPLENISHMENT_POLICY_STATUS_DESCRIPTORS: readonly StatusDescriptor[] = [
  {
    code: DRAFT_POLICY_STATUS,
    label: 'ร่าง (DRAFT)',
    summary: 'เขียนแล้วแต่ยังไม่มีผล — รออนุมัติซึ่งต้องไม่ใช่ผู้เขียนเอง (D-010)',
  },
  {
    code: ACTIVE_POLICY_STATUS,
    label: 'ใช้งานอยู่ (ACTIVE)',
    summary: 'กติกาเติมของของ flow นี้ในตอนนี้ — flow ละไม่เกินหนึ่งใบพร้อมกัน',
  },
  {
    code: SUPERSEDED_POLICY_STATUS,
    label: 'ถูกแทนที่แล้ว (SUPERSEDED)',
    summary: 'มี revision ใหม่กว่าถูกอนุมัติไปแล้ว — สถานะนี้เขียนโดยการอนุมัติใบถัดไป ไม่ใช่ปุ่มบนหน้าจอนี้',
  },
  {
    code: CANCELLED_POLICY_STATUS,
    label: 'ยกเลิกแล้ว (CANCELLED)',
    summary: 'ถอนร่างก่อนอนุมัติ — ใบยังอ่านได้ตลอดไป',
  },
];

export type StatusTone = 'neutral' | 'warning' | 'success' | 'info' | 'danger';

export function policyStatusTone(status: string): StatusTone {
  switch (status) {
    case DRAFT_POLICY_STATUS:
      return 'warning';
    case ACTIVE_POLICY_STATUS:
      return 'success';
    case SUPERSEDED_POLICY_STATUS:
      return 'info';
    case CANCELLED_POLICY_STATUS:
      return 'neutral';
    default:
      return 'neutral';
  }
}

export function policyStatusLabel(status: string): string {
  return (
    REPLENISHMENT_POLICY_STATUS_DESCRIPTORS.find((entry) => entry.code === status)?.label ??
    status
  );
}

export function policyModeLabel(mode: string): string {
  switch (mode) {
    case PUSH_MODE:
      return 'Push';
    case PULL_MODE:
      return 'Pull (Kanban)';
    default:
      return mode;
  }
}

/**
 * Policy ในสถานะนี้มี transition อะไรให้กระทำได้บ้าง — **เฉพาะ `DRAFT`**
 *
 * `ACTIVE`/`SUPERSEDED`/`CANCELLED` ไม่มีปุ่มเลย: `ACTIVE` เปลี่ยนได้ทางเดียวคือเขียน
 * revision ใหม่แล้วอนุมัติทับ (ไม่ใช่ปุ่มบนใบเดิม), ส่วนอีกสองเป็นสถานะปลายทาง
 *
 * ⚠️ **ไม่ตรวจว่าใครเป็นผู้สร้าง** — D-010 ปล่อยให้เซิร์ฟเวอร์ปฏิเสธและหน้าจอ
 * render คำปฏิเสธนั้น ไม่ใช่ซ่อนปุ่มไปก่อน
 */
export function policyLifecycleActionsFor(status: string): readonly PolicyTransition[] {
  switch (status) {
    case DRAFT_POLICY_STATUS:
      return ['approve', 'cancel'];
    default:
      return [];
  }
}

export function policyActionPermission(transition: PolicyTransition): 'EDIT' | 'APPROVE' {
  return transition === 'approve' ? 'APPROVE' : 'EDIT';
}

export function policyActionLabel(transition: PolicyTransition): string {
  switch (transition) {
    case 'approve':
      return 'อนุมัติ';
    case 'cancel':
      return 'ยกเลิกร่าง';
  }
}

export function policyActionTargetStatus(transition: PolicyTransition): string {
  switch (transition) {
    case 'approve':
      return ACTIVE_POLICY_STATUS;
    case 'cancel':
      return CANCELLED_POLICY_STATUS;
  }
}

/** กรองรายการด้วยคำค้นเดียว — เทียบรหัสสินค้า โรงงาน ต้นทาง และปลายทาง */
export function filterReplenishmentPolicies(
  items: readonly ReplenishmentPolicy[],
  query: string,
): readonly ReplenishmentPolicy[] {
  const needle = query.trim().toLocaleLowerCase();
  if (needle === '') {
    return items;
  }
  return items.filter(
    (policy) =>
      policy.product_code.toLocaleLowerCase().includes(needle) ||
      policy.plant_code.toLocaleLowerCase().includes(needle) ||
      policy.source_location_code.toLocaleLowerCase().includes(needle) ||
      policy.destination_location_code.toLocaleLowerCase().includes(needle),
  );
}

/**
 * จำนวนที่กรอกใช้ได้หรือยัง — เป็นสตริงล้วน ไม่แปลงเป็น number แม้แต่ครั้งเดียว
 * (เหตุผลเดียวกับ `isPositiveQuantity` ของ `procurementView.ts` — `NUMERIC(18, 4)`)
 */
export function isPositiveDecimal(value: string): boolean {
  const trimmed = value.trim();
  if (!/^\d{1,14}(\.\d{1,4})?$/.test(trimmed)) {
    return false;
  }
  return /[1-9]/.test(trimmed);
}

/** `card_count` เป็นจำนวนเต็มบวกจริง ๆ (ไม่ใช่ปริมาณต่อเนื่อง) */
export function isPositiveInteger(value: string): boolean {
  return /^[1-9]\d*$/.test(value.trim());
}

/** `YYYY-MM-DD` ที่มีอยู่จริงตามปฏิทิน — คืน `null` เมื่อรูปแบบผิดหรือวันที่ไม่มีจริง */
export function isValidCalendarDate(value: string): boolean {
  const trimmed = value.trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (match === null) {
    return false;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(year, month - 1, day);
  return (
    parsed.getFullYear() === year &&
    parsed.getMonth() === month - 1 &&
    parsed.getDate() === day
  );
}

/** ค่าที่ฟอร์มสร้าง policy ถืออยู่ — ดิบ ๆ ตามที่ผู้ใช้พิมพ์ ยังไม่ผ่าน `trim()` */
export interface CreateReplenishmentPolicyFields {
  readonly plantCode: string;
  readonly productCode: string;
  readonly sourceLocationCode: string;
  readonly destinationLocationCode: string;
  readonly upstreamWorkCenterCode: string;
  readonly downstreamWorkCenterCode: string;
  readonly mode: string;
  readonly containerQty: string;
  readonly cardCount: string;
  /** `<input type="date">` — `YYYY-MM-DD` */
  readonly effectiveFrom: string;
  readonly effectiveTo: string;
  readonly note: string;
}

/**
 * ประกอบ body ของ `POST /v1/replenishment-policies` — คืน `null` เมื่อยังประกอบไม่ได้
 *
 * ทุกรหัสถูก `trim()` ด้วยเหตุผลเดียวกับ `toCreatePurchaseRequisitionBody`
 * `effective_from`/`effective_to` ส่งเป็น `YYYY-MM-DD` ดิบ ๆ — เป็น `date` ไม่ใช่
 * `datetime` ฝั่งเซิร์ฟเวอร์ (ต่างจาก `required_date` ของใบขอซื้อ) จึงไม่ต้องแปลงเป็น
 * instant ที่มีเขตเวลา
 */
export function toCreateReplenishmentPolicyBody(
  fields: CreateReplenishmentPolicyFields,
): CreateReplenishmentPolicyBody | null {
  const plantCode = fields.plantCode.trim();
  const productCode = fields.productCode.trim();
  const sourceLocationCode = fields.sourceLocationCode.trim();
  const destinationLocationCode = fields.destinationLocationCode.trim();
  const mode = fields.mode.trim();
  const containerQty = fields.containerQty.trim();
  const cardCount = fields.cardCount.trim();
  const effectiveFrom = fields.effectiveFrom.trim();
  const effectiveTo = fields.effectiveTo.trim();

  if (
    plantCode === '' ||
    productCode === '' ||
    sourceLocationCode === '' ||
    destinationLocationCode === '' ||
    (mode !== PUSH_MODE && mode !== PULL_MODE) ||
    !isPositiveDecimal(containerQty) ||
    !isPositiveInteger(cardCount) ||
    !isValidCalendarDate(effectiveFrom) ||
    (effectiveTo !== '' && !isValidCalendarDate(effectiveTo)) ||
    (effectiveTo !== '' && effectiveTo < effectiveFrom)
  ) {
    return null;
  }

  const upstream = fields.upstreamWorkCenterCode.trim();
  const downstream = fields.downstreamWorkCenterCode.trim();
  const note = fields.note.trim();

  return {
    plant_code: plantCode,
    product_code: productCode,
    source_location_code: sourceLocationCode,
    destination_location_code: destinationLocationCode,
    ...(upstream === '' ? {} : { upstream_work_center_code: upstream }),
    ...(downstream === '' ? {} : { downstream_work_center_code: downstream }),
    mode: mode as CreateReplenishmentPolicyBody['mode'],
    container_qty: containerQty,
    card_count: Number(cardCount),
    effective_from: effectiveFrom,
    ...(effectiveTo === '' ? {} : { effective_to: effectiveTo }),
    ...(note === '' ? {} : { note }),
  };
}

/* -------------------------------------------------------- control cycle / card - */

export function cycleStatusTone(status: string): StatusTone {
  switch (status) {
    case ACTIVE_CYCLE_STATUS:
      return 'success';
    case SUPERSEDED_CYCLE_STATUS:
      return 'info';
    case CANCELLED_CYCLE_STATUS:
      return 'neutral';
    default:
      return 'neutral';
  }
}

export function cardStatusTone(status: string): StatusTone {
  switch (status) {
    case AVAILABLE_CARD_STATUS:
      return 'success';
    case EMPTY_CARD_STATUS:
      return 'warning';
    case REPLENISHING_CARD_STATUS:
      return 'info';
    case FULL_CARD_STATUS:
      return 'success';
    case BLOCKED_CARD_STATUS:
      return 'danger';
    default:
      return 'neutral';
  }
}

/** เขียนได้เฉพาะเมื่อมี cycle ที่ `ACTIVE` อยู่ — ตรงกับด่านจริง (`409 CYCLE_IS_NOT_ACTIVE`) */
export function canIssueCardOn(cycleStatus: string): boolean {
  return cycleStatus === ACTIVE_CYCLE_STATUS;
}

/* ======================================================= Kanban Signal (D-102) === */

export const KANBAN_SIGNAL_STATUS_DESCRIPTORS: readonly StatusDescriptor[] = [
  {
    code: NEW_SIGNAL_STATUS,
    label: 'ยกสัญญาณแล้ว (NEW)',
    summary: 'สแกนการ์ดเบิกแล้ว รอมีคนรับทราบ — การ์ดอยู่ที่ EMPTY',
  },
  {
    code: ACKNOWLEDGED_SIGNAL_STATUS,
    label: 'รับทราบแล้ว (ACKNOWLEDGED)',
    summary: 'มีคนรับผิดชอบสัญญาณนี้แล้ว — ยังไม่มีอะไรเคลื่อนสต๊อก',
  },
  {
    code: IN_REPLENISHMENT_SIGNAL_STATUS,
    label: 'กำลังเติม (IN_REPLENISHMENT)',
    summary: 'มีเอกสารระบุแล้วว่าเติมด้วยอะไร — การ์ดอยู่ที่ REPLENISHING',
  },
  {
    code: REPLENISHED_SIGNAL_STATUS,
    label: 'เติมสำเร็จแล้ว (REPLENISHED)',
    summary: 'ของเข้าภาชนะจริงพร้อม lot — จบ lifecycle แล้ว ยกเลิกไม่ได้อีก',
  },
  {
    code: CANCELLED_SIGNAL_STATUS,
    label: 'ยกเลิกแล้ว (CANCELLED)',
    summary: 'ถอนสัญญาณพร้อมเหตุผลที่บันทึกไว้ — จบ lifecycle แล้ว ยกเลิกซ้ำไม่ได้',
  },
];

export function signalStatusTone(status: string): StatusTone {
  switch (status) {
    case NEW_SIGNAL_STATUS:
      return 'warning';
    case ACKNOWLEDGED_SIGNAL_STATUS:
      return 'info';
    case IN_REPLENISHMENT_SIGNAL_STATUS:
      return 'info';
    case REPLENISHED_SIGNAL_STATUS:
      return 'success';
    case CANCELLED_SIGNAL_STATUS:
      return 'neutral';
    default:
      return 'neutral';
  }
}

export function signalStatusLabel(status: string): string {
  return (
    KANBAN_SIGNAL_STATUS_DESCRIPTORS.find((entry) => entry.code === status)?.label ?? status
  );
}

/**
 * Signal ในสถานะนี้มี transition อะไรให้กระทำได้บ้าง — **state machine ทั้งอันอยู่
 * ในฟังก์ชันเดียวนี้**
 *
 * `NEW` → รับทราบ หรือ ยกเลิก
 * `ACKNOWLEDGED` → เริ่มเติม หรือ ยกเลิก
 * `IN_REPLENISHMENT` → เติมของ หรือ ยกเลิก
 * `REPLENISHED`/`CANCELLED` → **ไม่มีเลย** — จบ lifecycle แล้ว (`422
 * SIGNAL_IS_NOT_OPEN` คือคำตอบของเซิร์ฟเวอร์ถ้าปุ่มที่ไม่ควรมีถูกกดอยู่ดี)
 */
export function signalLifecycleActionsFor(
  status: string,
): readonly KanbanSignalTransition[] {
  switch (status) {
    case NEW_SIGNAL_STATUS:
      return ['acknowledge', 'cancel'];
    case ACKNOWLEDGED_SIGNAL_STATUS:
      return ['start-replenishment', 'cancel'];
    case IN_REPLENISHMENT_SIGNAL_STATUS:
      return ['replenish', 'cancel'];
    default:
      return [];
  }
}

/** ทุก transition ของกลุ่มนี้ถือ `EDIT` — `CREATE` เป็นของ "สแกนเบิก" (`empty`) เท่านั้น */
export function signalActionPermission(transition: KanbanSignalTransition): 'EDIT' {
  switch (transition) {
    case 'acknowledge':
    case 'start-replenishment':
    case 'replenish':
    case 'cancel':
      return 'EDIT';
  }
}

export function signalActionLabel(transition: KanbanSignalTransition): string {
  switch (transition) {
    case 'acknowledge':
      return 'รับทราบ';
    case 'start-replenishment':
      return 'เริ่มเติม';
    case 'replenish':
      return 'เติมของแล้ว';
    case 'cancel':
      return 'ยกเลิกสัญญาณ';
  }
}

export function signalActionTargetStatus(transition: KanbanSignalTransition): string {
  switch (transition) {
    case 'acknowledge':
      return ACKNOWLEDGED_SIGNAL_STATUS;
    case 'start-replenishment':
      return IN_REPLENISHMENT_SIGNAL_STATUS;
    case 'replenish':
      return REPLENISHED_SIGNAL_STATUS;
    case 'cancel':
      return CANCELLED_SIGNAL_STATUS;
  }
}

/**
 * transition นี้เคลื่อนสต๊อกจริงหรือไม่ — **มีแต่ `replenish` เท่านั้นที่ใช่**
 * (headline ของ `WO-P2-M7-006`: signal คือคำขอให้เติมของ ไม่ใช่การเคลื่อนสต๊อก)
 */
export function movesInventory(transition: KanbanSignalTransition): boolean {
  return transition === 'replenish';
}

/** กรองรายการด้วยคำค้นเดียว — เทียบรหัสการ์ดและรหัสสินค้า */
export function filterKanbanSignals(
  items: readonly KanbanSignal[],
  query: string,
): readonly KanbanSignal[] {
  const needle = query.trim().toLocaleLowerCase();
  if (needle === '') {
    return items;
  }
  return items.filter(
    (signal) =>
      signal.card_code.toLocaleLowerCase().includes(needle) ||
      signal.product_code.toLocaleLowerCase().includes(needle),
  );
}

/** เหตุผลยกเลิกใช้ได้หรือยัง — ต้องไม่ว่าง (WO-P2-M7-005: ห้ามส่งสตริงว่าง) */
export function isValidCancellationReason(value: string): boolean {
  return value.trim() !== '';
}

/** หนึ่งบรรทัด lot ที่ฟอร์มเติมของถืออยู่ — ดิบ ๆ ตามที่ผู้ใช้พิมพ์ */
export interface KanbanLotDraft {
  readonly lotId: string;
  readonly qty: string;
}

/**
 * ประกอบรายการ lot ของ `replenish` — คืน `null` เมื่อยังประกอบไม่ได้
 *
 * ต้องมีอย่างน้อยหนึ่งบรรทัด (D-102 ข้อ 6) และทุกบรรทัดต้องมี `lot_id` และปริมาณบวก —
 * บรรทัดเดียวที่ผิดรูปทำให้**ทั้งคำขอ**ประกอบไม่ได้ ไม่ใช่ถูกตัดทิ้งเงียบ ๆ
 */
export function toKanbanContainerLotInputs(
  drafts: readonly KanbanLotDraft[],
): readonly KanbanContainerLotInput[] | null {
  if (drafts.length === 0) {
    return null;
  }
  const lots: KanbanContainerLotInput[] = [];
  for (const draft of drafts) {
    const lotId = draft.lotId.trim();
    const qty = draft.qty.trim();
    if (lotId === '' || !isPositiveDecimal(qty)) {
      return null;
    }
    lots.push({ lot_id: lotId, qty });
  }
  return lots;
}
